import React, { useMemo } from "react";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  QuestionMarkCircleIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { toast } from "react-toastify";
import { renderAmount } from "../../../utils";
import Checkbox from "../../../shared/Checkbox";
import Button from "../../../shared/Button";
import Input from "../../../shared/Input";
import Popover from "../../../shared/Popover";
import { Select } from "../../../shared/Select";
import Spinner from "../../../shared/Spinner";
import TextArea from "../../../shared/TextArea";
import LineItemsEditor from "./LineItemsEditor";
import InvoiceSummary from "./InvoiceSummary";
import {
  TAX_MODE_OPTIONS,
  TERMS_OPTIONS,
  STATUS_OPTIONS,
  buildCreditBreakdown,
  buildFeeBreakdown,
  buildSourceRefs,
  createCredit,
  createFee,
  createDiscount,
  buildDiscountBreakdown,
  extractClients,
  toNumber,
} from "../utils";

const HelpTooltip = ({ content }) => (
  <Popover
    content={<div className="max-w-64 text-xs leading-5 text-gray-700">{content}</div>}
    hover
    position="top"
    trigger={
      <span
        className="inline-flex h-4 w-4 cursor-help items-center justify-center text-gray-400 hover:text-gray-600"
        aria-label="More information"
      >
        <QuestionMarkCircleIcon className="h-4 w-4" />
      </span>
    }
  />
);

const IconActionButton = ({
  label,
  icon: Icon,
  onClick,
  disabled = false,
  tone = "default",
}) => (
  <button
    type="button"
    className={`inline-flex h-8 w-8 items-center justify-center rounded-full border p-2 transition ${
      tone === "danger"
        ? "border-red-200 text-red-500 hover:border-red-300 hover:bg-red-50 hover:text-red-600"
        : "border-gray-200 text-gray-500 hover:border-gray-300 hover:bg-gray-100 hover:text-gray-700"
    } ${disabled ? "cursor-not-allowed opacity-40" : "cursor-pointer"}`}
    onClick={onClick}
    disabled={disabled}
    title={label}
    aria-label={label}
  >
    <Icon className="h-4 w-4" />
  </button>
);

const InvoiceV2Editor = ({
  title,
  subtitle,
  /** Rendered on the same row as the title (e.g. inline tags like Job show). */
  titleInlineExtras,
  headerExtras,
  clientsData,
  clientsLoading,
  jobs,
  jobsLoading,
  client,
  onClientChange,
  clientLocked = false,
  status,
  onStatusChange,
  terms,
  onTermsChange,
  termsCode,
  onTermsCodeChange,
  netDays,
  onNetDaysChange,
  paymentInstructions,
  onPaymentInstructionsChange,
  dueDate,
  onDueDateChange,
  taxSettings,
  onTaxSettingsChange,
  lineItems,
  onLineItemChange,
  onLineItemRemove,
  onAddManualLine,
  onAddJobLines,
  discounts = [],
  onDiscountsChange = () => {},
  fees = [],
  onFeesChange = () => {},
  credits = [],
  onCreditsChange = () => {},
  totals,
  submitLabel,
  onSubmit,
  submitting,
  backPath = "/invoices/v2",
  onBack,
}) => {
  const clients = useMemo(() => extractClients(clientsData), [clientsData]);
  const clientOptions = useMemo(
    () =>
      clients.map((item) => ({
        label: item.name,
        value: item,
      })),
    [clients]
  );
  const sourceRefs = useMemo(
    () => buildSourceRefs(lineItems, jobs),
    [jobs, lineItems]
  );
  const discountBreakdown = useMemo(
    () =>
      buildDiscountBreakdown(
        lineItems,
        discounts,
        fees,
        credits
      ).discountBreakdown,
    [credits, discounts, fees, lineItems]
  );
  const feeBreakdown = useMemo(
    () =>
      buildFeeBreakdown(
        lineItems,
        discounts,
        fees,
        credits
      ).feeBreakdown,
    [credits, discounts, fees, lineItems]
  );
  const creditBreakdown = useMemo(
    () =>
      buildCreditBreakdown(
        lineItems,
        discounts,
        fees,
        credits
      ).creditBreakdown,
    [credits, discounts, fees, lineItems]
  );

  function syncDiscounts(nextDiscounts = []) {
    onDiscountsChange(
      nextDiscounts.map((discount, index) => ({
        ...discount,
        applicationOrder: index,
      }))
    );
  }

  function updateDiscount(localId, updates) {
    syncDiscounts(
      discounts.map((item) =>
        item.localId === localId ? { ...item, ...updates } : item
      )
    );
  }

  function removeDiscount(localId) {
    syncDiscounts(discounts.filter((item) => item.localId !== localId));
  }

  function moveDiscount(localId, direction) {
    const currentDiscounts = [...discounts];
    const currentIndex = currentDiscounts.findIndex((item) => item.localId === localId);

    if (currentIndex < 0) {
      return;
    }

    const nextIndex = currentIndex + direction;
    if (nextIndex < 0 || nextIndex >= currentDiscounts.length) {
      return;
    }

    const [movedDiscount] = currentDiscounts.splice(currentIndex, 1);
    currentDiscounts.splice(nextIndex, 0, movedDiscount);
    syncDiscounts(currentDiscounts);
  }

  function syncFees(nextFees = []) {
    onFeesChange(
      nextFees.map((fee, index) => ({
        ...fee,
        applicationOrder: index,
      }))
    );
  }

  function updateFee(localId, updates) {
    syncFees(fees.map((item) => (item.localId === localId ? { ...item, ...updates } : item)));
  }

  function removeFee(localId) {
    syncFees(fees.filter((item) => item.localId !== localId));
  }

  function moveFee(localId, direction) {
    const currentFees = [...fees];
    const currentIndex = currentFees.findIndex((item) => item.localId === localId);

    if (currentIndex < 0) {
      return;
    }

    const nextIndex = currentIndex + direction;
    if (nextIndex < 0 || nextIndex >= currentFees.length) {
      return;
    }

    const [movedFee] = currentFees.splice(currentIndex, 1);
    currentFees.splice(nextIndex, 0, movedFee);
    syncFees(currentFees);
  }

  function syncCredits(nextCredits = []) {
    onCreditsChange(
      nextCredits.map((credit, index) => ({
        ...credit,
        applicationOrder: index,
      }))
    );
  }

  function updateCredit(localId, updates) {
    syncCredits(
      credits.map((item) =>
        item.localId === localId ? { ...item, ...updates } : item
      )
    );
  }

  function removeCredit(localId) {
    syncCredits(credits.filter((item) => item.localId !== localId));
  }

  function moveCredit(localId, direction) {
    const currentCredits = [...credits];
    const currentIndex = currentCredits.findIndex((item) => item.localId === localId);

    if (currentIndex < 0) {
      return;
    }

    const nextIndex = currentIndex + direction;
    if (nextIndex < 0 || nextIndex >= currentCredits.length) {
      return;
    }

    const [movedCredit] = currentCredits.splice(currentIndex, 1);
    currentCredits.splice(nextIndex, 0, movedCredit);
    syncCredits(currentCredits);
  }

  async function handleSubmit() {
    if (!client?._id) {
      toast.error("Select a client before saving the invoice", {
        toastId: "invoice-v2-client-required",
      });
      return;
    }

    if (!Array.isArray(lineItems) || lineItems.length === 0) {
      toast.error("Add at least one line item before saving the invoice", {
        toastId: "invoice-v2-line-items-required",
      });
      return;
    }

    await onSubmit();
  }

  return (
    <div className="space-y-6 px-4 py-6 md:px-6">
      <div>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <h1 className="text-2xl font-semibold text-gray-900">{title}</h1>
          {titleInlineExtras || null}
        </div>
        {subtitle ? <p className="mt-1 text-sm text-gray-500">{subtitle}</p> : null}
        {headerExtras ? <div className="mt-4">{headerExtras}</div> : null}
      </div>

      <div className="grid grid-cols-1 gap-4 rounded-lg border border-gray-200 bg-white p-4 shadow-sm md:grid-cols-2">
        <Select
          id="client"
          label="Client"
          value={client?.name || ""}
          options={clientOptions}
          onChange={(event) => onClientChange(event.value)}
          loading={clientsLoading}
          disabled={clientLocked}
        />
        <Select
          id="status"
          label="Status"
          value={
            STATUS_OPTIONS.find((option) => option.value === status)?.label || ""
          }
          options={STATUS_OPTIONS}
          onChange={(event) => onStatusChange(event.value)}
        />
        <Select
          id="terms-code"
          label="Payment terms"
          value={TERMS_OPTIONS.find((option) => option.value === termsCode)?.label || ""}
          options={TERMS_OPTIONS}
          onChange={(event) => onTermsCodeChange(event.value)}
        />
        <Input
          label="Due date"
          type="date"
          value={dueDate}
          onChange={(target) => onDueDateChange(target.value)}
        />
        {termsCode === "custom" ? (
          <>
            <Input
              label="Custom terms (customer-facing)"
              value={terms}
              onChange={(target) => onTermsChange(target.value)}
            />
            <Input
              label="Net days"
              type="number"
              value={netDays ?? ""}
              onChange={(target) => onNetDaysChange(toNumber(target.value))}
            />
          </>
        ) : null}
        <TextArea
          label="Payment instructions"
          value={paymentInstructions}
          onChange={(event) => onPaymentInstructionsChange(event.target.value)}
          wrapperClasses="mb-0 md:col-span-2"
        />
      </div>

      <div className="grid grid-cols-1 gap-4 rounded-lg border border-gray-200 bg-white p-4 shadow-sm md:grid-cols-4">
        <Select
          id="tax-mode"
          label="Tax mode"
          value={
            TAX_MODE_OPTIONS.find((option) => option.value === taxSettings?.mode)?.label || ""
          }
          options={TAX_MODE_OPTIONS}
          onChange={(event) =>
            onTaxSettingsChange({
              ...taxSettings,
              mode: event.value,
            })
          }
        />
        <Input
          label="Default tax rate"
          type="number"
          value={taxSettings?.defaultTaxRate ?? 0}
          onChange={(target) =>
            onTaxSettingsChange({
              ...taxSettings,
              defaultTaxRate: toNumber(target.value),
            })
          }
        />
        <Input
          label="Default tax code"
          value={taxSettings?.defaultTaxCode || ""}
          onChange={(target) =>
            onTaxSettingsChange({
              ...taxSettings,
              defaultTaxCode: target.value,
            })
          }
        />
        <div className="flex items-end pb-3">
          <Checkbox
            id="invoice-v2-default-taxable"
            checked={Boolean(taxSettings?.defaultTaxable)}
            label="Default taxable"
            onChange={(target) =>
              onTaxSettingsChange({
                ...taxSettings,
                defaultTaxable: Boolean(target.checked),
              })
            }
          />
        </div>
      </div>

      {clientsLoading ? (
        <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <Spinner size="md" />
          <span className="text-sm text-gray-600">Loading clients...</span>
        </div>
      ) : client ? (
        <LineItemsEditor
          lineItems={lineItems}
          jobs={jobs}
          onChange={onLineItemChange}
          onRemove={onLineItemRemove}
          onAddManual={onAddManualLine}
          onAddJobs={onAddJobLines}
        />
      ) : (
        <div className="rounded-lg border border-dashed border-gray-300 bg-white p-6 text-sm text-gray-500">
          Select a client to start building invoice lines.
        </div>
      )}

      {client && jobsLoading ? (
        <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <Spinner size="md" />
          <span className="text-sm text-gray-600">Loading jobs...</span>
        </div>
      ) : null}

      <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-1.5">
              <h2 className="text-base font-semibold text-gray-900">Discounts</h2>
              <HelpTooltip content="Discounts reduce the invoice total. Pre-tax discounts reduce the taxable base before tax is calculated, while post-tax discounts are applied after tax." />
            </div>
            <p className="mt-0.5 text-xs text-gray-500">
              Add named invoice-level discounts as flat amounts or percentages.
            </p>
          </div>
          <Button
            type="alternative"
            size="sm"
            onClick={() => syncDiscounts([...discounts, createDiscount()])}
          >
            Add discount
          </Button>
        </div>

        {discounts?.length ? (
          <div className="space-y-2">
            <div className="hidden md:grid md:grid-cols-[2fr,0.95fr,0.9fr,0.95fr,auto] md:gap-2 md:px-2 md:text-[11px] md:font-semibold md:uppercase md:tracking-wide md:text-gray-500">
              <span>Name</span>
              <span>Type</span>
              <span>Amount</span>
              <span>Timing</span>
              <span className="text-right">Actions</span>
            </div>
            {discounts.map((discount, index) => (
              <div
                key={discount.localId || discount.discountId}
                className="grid grid-cols-1 gap-2 rounded-lg border border-gray-100 bg-gray-50 p-2.5 md:grid-cols-[2fr,0.95fr,0.9fr,0.95fr,auto]"
              >
                <Input
                  label={<span className="md:sr-only">Name</span>}
                  size="sm"
                  wrapperClasses="mb-0"
                  value={discount.name}
                  placeholder="Discount name"
                  onChange={(target) => updateDiscount(discount.localId, { name: target.value })}
                />
                <Select
                  id={`discount-type-${discount.localId}`}
                  label={<span className="md:sr-only">Type</span>}
                  size="sm"
                  wrapperClasses="mb-0"
                  value={discount.type === "percentage" ? "Percentage" : "Flat"}
                  options={[
                    { label: "Flat", value: "flat" },
                    { label: "Percentage", value: "percentage" },
                  ]}
                  onChange={(event) => updateDiscount(discount.localId, { type: event.value })}
                />
                <Input
                  label={<span className="md:sr-only">Amount</span>}
                  type="number"
                  size="sm"
                  wrapperClasses="mb-0"
                  value={discount.value}
                  placeholder={discount.type === "percentage" ? "Percent" : "Amount"}
                  onChange={(target) =>
                    updateDiscount(discount.localId, { value: toNumber(target.value) })
                  }
                />
                <Select
                  id={`discount-timing-${discount.localId}`}
                  label={<span className="md:sr-only">Timing</span>}
                  size="sm"
                  wrapperClasses="mb-0"
                  value={discount.timing === "postTax" ? "Post-tax" : "Pre-tax"}
                  options={[
                    { label: "Pre-tax", value: "preTax" },
                    { label: "Post-tax", value: "postTax" },
                  ]}
                  onChange={(event) =>
                    updateDiscount(discount.localId, { timing: event.value })
                  }
                />
                <div className="flex items-end gap-1.5 md:justify-end">
                  <IconActionButton
                    label="Move discount up"
                    icon={ArrowUpIcon}
                    onClick={() => moveDiscount(discount.localId, -1)}
                    disabled={index === 0}
                  />
                  <IconActionButton
                    label="Move discount down"
                    icon={ArrowDownIcon}
                    onClick={() => moveDiscount(discount.localId, 1)}
                    disabled={index === discounts.length - 1}
                  />
                  <IconActionButton
                    label="Remove discount"
                    icon={TrashIcon}
                    tone="danger"
                    onClick={() => removeDiscount(discount.localId)}
                  />
                </div>
                {discountBreakdown[index] ? (
                  <div className="md:col-span-5 text-xs text-gray-500">
                    Applied amount preview:{" "}
                    <span className="font-medium text-gray-900">
                      {renderAmount(discountBreakdown[index].appliedAmount || 0)}
                    </span>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">No discounts added yet.</p>
        )}
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-1.5">
              <h2 className="text-base font-semibold text-gray-900">Fees</h2>
              <HelpTooltip content="Fees add extra charges to the invoice. Pre-tax fees increase the amount before tax is calculated, while post-tax fees are added after tax." />
            </div>
            <p className="mt-0.5 text-xs text-gray-500">
              Add named invoice-level fees as flat amounts or percentages.
            </p>
          </div>
          <Button
            type="alternative"
            size="sm"
            onClick={() => syncFees([...fees, createFee()])}
          >
            Add fee
          </Button>
        </div>

        {fees?.length ? (
          <div className="space-y-2">
            <div className="hidden md:grid md:grid-cols-[2fr,0.95fr,0.9fr,0.95fr,auto] md:gap-2 md:px-2 md:text-[11px] md:font-semibold md:uppercase md:tracking-wide md:text-gray-500">
              <span>Name</span>
              <span>Type</span>
              <span>Amount</span>
              <span>Timing</span>
              <span className="text-right">Actions</span>
            </div>
            {fees.map((fee, index) => (
              <div
                key={fee.localId || fee.feeId}
                className="grid grid-cols-1 gap-2 rounded-lg border border-gray-100 bg-gray-50 p-2.5 md:grid-cols-[2fr,0.95fr,0.9fr,0.95fr,auto]"
              >
                <Input
                  label={<span className="md:sr-only">Name</span>}
                  size="sm"
                  wrapperClasses="mb-0"
                  value={fee.name}
                  placeholder="Fee name"
                  onChange={(target) => updateFee(fee.localId, { name: target.value })}
                />
                <Select
                  id={`fee-type-${fee.localId}`}
                  label={<span className="md:sr-only">Type</span>}
                  size="sm"
                  wrapperClasses="mb-0"
                  value={fee.type === "percentage" ? "Percentage" : "Flat"}
                  options={[
                    { label: "Flat", value: "flat" },
                    { label: "Percentage", value: "percentage" },
                  ]}
                  onChange={(event) => updateFee(fee.localId, { type: event.value })}
                />
                <Input
                  label={<span className="md:sr-only">Amount</span>}
                  type="number"
                  size="sm"
                  wrapperClasses="mb-0"
                  value={fee.value}
                  placeholder={fee.type === "percentage" ? "Percent" : "Amount"}
                  onChange={(target) => updateFee(fee.localId, { value: toNumber(target.value) })}
                />
                <Select
                  id={`fee-timing-${fee.localId}`}
                  label={<span className="md:sr-only">Timing</span>}
                  size="sm"
                  wrapperClasses="mb-0"
                  value={fee.timing === "preTax" ? "Pre-tax" : "Post-tax"}
                  options={[
                    { label: "Pre-tax", value: "preTax" },
                    { label: "Post-tax", value: "postTax" },
                  ]}
                  onChange={(event) => updateFee(fee.localId, { timing: event.value })}
                />
                <div className="flex items-end gap-1.5 md:justify-end">
                  <IconActionButton
                    label="Move fee up"
                    icon={ArrowUpIcon}
                    onClick={() => moveFee(fee.localId, -1)}
                    disabled={index === 0}
                  />
                  <IconActionButton
                    label="Move fee down"
                    icon={ArrowDownIcon}
                    onClick={() => moveFee(fee.localId, 1)}
                    disabled={index === fees.length - 1}
                  />
                  <IconActionButton
                    label="Remove fee"
                    icon={TrashIcon}
                    tone="danger"
                    onClick={() => removeFee(fee.localId)}
                  />
                </div>
                {feeBreakdown[index] ? (
                  <div className="md:col-span-5 text-xs text-gray-500">
                    Applied amount preview:{" "}
                    <span className="font-medium text-gray-900">
                      {renderAmount(feeBreakdown[index].appliedAmount || 0)}
                    </span>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">No fees added yet.</p>
        )}
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-sm">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-1.5">
              <h2 className="text-base font-semibold text-gray-900">Credits</h2>
              <HelpTooltip content="Credits reduce the invoice balance. Use them for account credits, returns, or goodwill adjustments, either before tax or after tax." />
            </div>
            <p className="mt-0.5 text-xs text-gray-500">
              Add named invoice-level credits as flat amounts or percentages.
            </p>
          </div>
          <Button
            type="alternative"
            size="sm"
            onClick={() => syncCredits([...credits, createCredit()])}
          >
            Add credit
          </Button>
        </div>

        {credits?.length ? (
          <div className="space-y-2">
            <div className="hidden md:grid md:grid-cols-[2fr,0.95fr,0.9fr,0.95fr,auto] md:gap-2 md:px-2 md:text-[11px] md:font-semibold md:uppercase md:tracking-wide md:text-gray-500">
              <span>Name</span>
              <span>Type</span>
              <span>Amount</span>
              <span>Timing</span>
              <span className="text-right">Actions</span>
            </div>
            {credits.map((credit, index) => (
              <div
                key={credit.localId || credit.creditId}
                className="grid grid-cols-1 gap-2 rounded-lg border border-gray-100 bg-gray-50 p-2.5 md:grid-cols-[2fr,0.95fr,0.9fr,0.95fr,auto]"
              >
                <Input
                  label={<span className="md:sr-only">Name</span>}
                  size="sm"
                  wrapperClasses="mb-0"
                  value={credit.name}
                  placeholder="Credit name"
                  onChange={(target) => updateCredit(credit.localId, { name: target.value })}
                />
                <Select
                  id={`credit-type-${credit.localId}`}
                  label={<span className="md:sr-only">Type</span>}
                  size="sm"
                  wrapperClasses="mb-0"
                  value={credit.type === "percentage" ? "Percentage" : "Flat"}
                  options={[
                    { label: "Flat", value: "flat" },
                    { label: "Percentage", value: "percentage" },
                  ]}
                  onChange={(event) => updateCredit(credit.localId, { type: event.value })}
                />
                <Input
                  label={<span className="md:sr-only">Amount</span>}
                  type="number"
                  size="sm"
                  wrapperClasses="mb-0"
                  value={credit.value}
                  placeholder={credit.type === "percentage" ? "Percent" : "Amount"}
                  onChange={(target) =>
                    updateCredit(credit.localId, { value: toNumber(target.value) })
                  }
                />
                <Select
                  id={`credit-timing-${credit.localId}`}
                  label={<span className="md:sr-only">Timing</span>}
                  size="sm"
                  wrapperClasses="mb-0"
                  value={credit.timing === "preTax" ? "Pre-tax" : "Post-tax"}
                  options={[
                    { label: "Pre-tax", value: "preTax" },
                    { label: "Post-tax", value: "postTax" },
                  ]}
                  onChange={(event) =>
                    updateCredit(credit.localId, { timing: event.value })
                  }
                />
                <div className="flex items-end gap-1.5 md:justify-end">
                  <IconActionButton
                    label="Move credit up"
                    icon={ArrowUpIcon}
                    onClick={() => moveCredit(credit.localId, -1)}
                    disabled={index === 0}
                  />
                  <IconActionButton
                    label="Move credit down"
                    icon={ArrowDownIcon}
                    onClick={() => moveCredit(credit.localId, 1)}
                    disabled={index === credits.length - 1}
                  />
                  <IconActionButton
                    label="Remove credit"
                    icon={TrashIcon}
                    tone="danger"
                    onClick={() => removeCredit(credit.localId)}
                  />
                </div>
                {creditBreakdown[index] ? (
                  <div className="md:col-span-5 text-xs text-gray-500">
                    Applied amount preview:{" "}
                    <span className="font-medium text-gray-900">
                      {renderAmount(creditBreakdown[index].appliedAmount || 0)}
                    </span>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500">No credits added yet.</p>
        )}
      </div>

      <InvoiceSummary
        totals={totals}
        sourceRefs={sourceRefs}
        discounts={discountBreakdown}
        fees={feeBreakdown}
        credits={creditBreakdown}
      />

      <div className="flex items-center justify-end gap-3 pb-24 pr-20">
        <Button type="alternative" onClick={onBack}>
          Back
        </Button>
        <Button onClick={handleSubmit} loading={submitting} disabled={!client || submitting}>
          {submitLabel}
        </Button>
      </div>
    </div>
  );
};

export default InvoiceV2Editor;
